import {
  CaretDownFilled,
  CaretRightFilled,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import TreeBranchLineIcon from '@/assets/icons/TreeBranchLineIcon';
import { Flex, Space } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import ColorSelector from '@/components/Common/ColorSelector';
import Input from '@/components/Inputs/Input';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { useInputs } from '@/hooks/pure/useInputs';
import usePopupStore from '@/stores/pure/PopupStore';
import useTagStore from '@/stores/TagStore';

import NewTagItem from './NewTagItem';
import { validateTagNameProperty } from './tagInputValidator';

type Props = {
  tag: TagT;
  hideList: string[];
  setHideList: React.Dispatch<React.SetStateAction<string[]>>;
};

const TagSettingItem = (props: Props) => {
  const color = useColor();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [tagNameError, setTagNameError] = useState<string | null>(null);
  const [isChildBeingAdded, setIsChildBeingAdded] = useState(false);

  const { tag, hideList, setHideList } = props;

  const { tagMap, deleteTag, changeTagInfo } = useTagStore((state) => ({
    tagMap: state.tagMap,
    deleteTag: state.deleteTag,
    changeTagInfo: state.changeTagInfo,
  }));
  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));

  const deleteTagHandler = async () => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: `Are you sure you want to delete "${tag.name}" tag?`,
      message:
        'The tag will be removed from all feedback. This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        deleteTag(tag.alias);
      },
    });
  };

  const [inputs, onChange, onCustomChange, initializeInputs] = useInputs({
    name: tag.name,
    color: tag.color || 1,
  });

  useEffect(() => {
    onChange({ target: { name: 'name', value: tag.name } });
    onCustomChange('color', tag.color);
  }, [tag, onChange, onCustomChange]);

  const handleTagNameInput = (e: { target: { name: string; value: any } }) => {
    const { value } = e.target;
    const siblingNames = tag.parent
      ? tagMap[tag.parent].children.map((alias) => tagMap[alias].name)
      : [];
    const errorMessage = validateTagNameProperty(value, siblingNames);
    if (errorMessage) {
      setTagNameError(errorMessage);
    } else {
      setTagNameError(null);
    }
    onChange(e);
  };

  const indentInPx = useMemo(() => {
    return tag.level === 1 ? 0 : tag.level === 2 ? 36 : 72;
  }, [tag]);

  const BranchIcon = useMemo(() => {
    return tag.level === 1
      ? hideList.includes(tag.alias)
        ? CaretRightFilled
        : CaretDownFilled
      : TreeBranchLineIcon;
  }, [tag, hideList]);

  const indent = `${indentInPx}px`;

  const toggleHideList = () => {
    if (hideList.includes(tag.alias)) {
      setHideList((state) => state.filter((h) => h !== tag.alias));
    } else {
      setHideList((state) => state.concat([tag.alias]));
    }
  };
  const root = useMemo(() => {
    if (!tag.parent) {
      return null;
    }
    if (tag.level === 3) {
      const parent2 = tagMap[tag.parent];
      if (parent2) {
        if (parent2.parent) {
          return parent2.parent;
        }
      }
    } else if (tag.level === 2) {
      const parent = tagMap[tag.parent];
      return parent.alias;
    }
    return null;
  }, [tag.level, tag.parent, tagMap]);

  if (root && hideList.includes(root)) {
    return <></>;
  }
  return (
    <Flex d="column">
      <Style a="center">
        <Col g="8px" a="center" style={{ flexGrow: 8 }}>
          <Space w={indent} />
          <IconsWrapper a="center" j="space-around" d="row">
            <BranchIcon onClick={toggleHideList} />
          </IconsWrapper>
          <ColorSelector
            value={inputs.color}
            onChange={(v) => {
              onCustomChange('color', v);
              changeTagInfo(tag.alias, inputs.name, v);
            }}
          />
          <Flex f={1}>
            {isEdit ? (
              <Flex f={1} g={8}>
                <Input
                  initFocus
                  sizeType="m"
                  placeholder="type tag name"
                  name="name"
                  value={inputs.name}
                  wrapperStyle={{ flex: 1 }}
                  onChange={(e) => handleTagNameInput(e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      changeTagInfo(tag.alias, inputs.name, inputs.color);
                      initializeInputs();
                      setIsEdit(false);
                    }
                    if (e.key === 'Escape') {
                      initializeInputs();
                      setIsEdit(false);
                    }
                  }}
                />
                <IconButton
                  icon={<CheckOutlined style={{ color: color.green500 }} />}
                  sizeType="m"
                  onClick={() => {
                    changeTagInfo(tag.alias, inputs.name, inputs.color);
                    initializeInputs();
                    setIsEdit(false);
                  }}
                />
                <IconButton
                  icon={<CloseOutlined />}
                  sizeType="m"
                  onClick={() => {
                    initializeInputs();
                    setIsEdit(false);
                  }}
                />
              </Flex>
            ) : (
              <Flex f={1} g={8} a="center">
                <Typo type="label2" onClick={() => setIsEdit(true)}>
                  {tag.name}
                </Typo>
                <IconButton
                  className="hover-item"
                  icon={<EditOutlined />}
                  sizeType="m"
                  onClick={() => setIsEdit(true)}
                />
              </Flex>
            )}
          </Flex>
          {tagNameError && (
            <Typo type="label3" color={color.red700}>
              {tagNameError}
            </Typo>
          )}
        </Col>
        <Col style={{ flexGrow: 1 }}></Col>
        {!isEdit && (
          <Col
            j="flex-end"
            g="8px"
            style={{ flexGrow: 1 }}
            className="hover-item"
          >
            {tag.level < 3 && (
              <IconButton
                sizeType="m"
                icon={<PlusOutlined />}
                onClick={() => setIsChildBeingAdded(true)}
                tooltip="Add Child Tag"
              />
            )}

            <IconButton
              sizeType="m"
              icon={<DeleteOutlined />}
              onClick={deleteTagHandler}
              tooltip="Delete Tag"
            />
          </Col>
        )}
      </Style>
      {isChildBeingAdded && (
        <NewTagItem
          parentTag={tag}
          isChildBeingAdded={isChildBeingAdded}
          setIsChildBeingAdded={setIsChildBeingAdded}
        />
      )}
    </Flex>
  );
};

const Style = styled(Flex)`
  position: relative;
  padding: 8px 20px;
  color: ${(props) => props.theme.colors.gray700};
  transition: all 0.2s;
  border-radius: 8px;
  .hover-item {
    transition: all 0.2s;
    opacity: 0;
  }
  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
    .hover-item {
      opacity: 1;
    }
  }
`;
const Col = styled(Flex)`
  flex: 1;
  padding-right: 8px;
`;

const IconsWrapper = styled(Flex)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.gray600};
`;

export default TagSettingItem;
